import React, { useState, useCallback, useEffect } from "react";
import List from "../workingPartList";
import Filter from "../workingPartFilter";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import PATHS from "../../../../constants/paths";
import { BreadCrumb } from "@de-prado-dev/react-component-library";
import * as UserServices from "../../../../services/UserServices";
import * as WorkingPartServices from "../../../../services/WorkingPartService";
import * as GeneralServices from "../../../../services/GeneralServices";
import moment from "moment-timezone";
import * as XLSX from "xlsx";
import { PART_STATUS, SET_FILTER_PART } from "../../../../constants";
import * as ViveroService from "../../../../services/ViveroService";
import {
  converToTime,
  msToHoursnumber,
} from "../../../../utils/formats/number";
import { useDailyPart } from "../hook/useDailyPart";
import { Can, AbilityContext } from "../../../security/permissions/Can";
import { USER_PERMISSIONS } from "../../../security/permissions/rolesPermission";
import { useSelector } from "react-redux";
import { getFilterPartSelector } from "../../../../store/selectors/navSelector";
import * as ArticleService from "../../../../services/ArticleService";
import * as LocationService from "../../../../services/LocationService";
import * as TaskService from "../../../../services/TaskService";
import * as BatchService from "../../../../services/BatchService";

const queryClient = new QueryClient();

const WorkingPartFilterListContainer = (props) => {
  const { isTechnical } = props;
  const filters = useSelector(getFilterPartSelector);

  const { t } = useTranslation();
  // const [filters, setFilters] = useState(undefined);
  const [viveros, setViveros] = useState([]);
  const breads = [
    { label: t("home"), path: PATHS.HOME },
    { label: t("forms.workingPart.title") },
  ];

  const { confirmAllDailyPart } = useDailyPart(() =>
    GeneralServices.setFilter({ test: "" }, SET_FILTER_PART)
  );

  const loadViveros = useCallback(async () => {
    const resultViveros = await ViveroService.getAllViveros();
    if (resultViveros) {
      setViveros(resultViveros);
    }
  }, []);

  useEffect(() => {
    loadViveros();
  }, [loadViveros]);

  const handlerCleanFilter = async (resetForm) => {
    resetForm();
    // setFilters(undefined);
    GeneralServices.setFilter(undefined, SET_FILTER_PART);
  };

  const handlerOnSubmitFilter = (values) => {
    let newFilter = {};
    const valuesFilters = Object.entries(values);
    valuesFilters.forEach((value) => {
      if (value[1] !== undefined && value[1].toString().length > 0) {
        newFilter[value[0]] = value[1];
      }
    });

    // setFilters(newFilter);
    GeneralServices.setFilter(newFilter, SET_FILTER_PART);
  };

  const handlerFilterUser = async (value) => {
    let filter = {
      filters: {
        name: value,
      },
    };

    let result = await UserServices.filterAllUser(filter);
    if (result && result.length === 0) {
      if (filters?.userUuid) {
        filter = {
          filters: {
            uuid: filters.userUuid,
          },
        };
        result = await UserServices.filterAllUser(filter);
      }
    }
    return result;
  };

  const handlerFilterArticle = async (value) => {
    let filter = {
      filters: {
        name: value,
      },
    };

    let result = await ArticleService.getAllArticles(filter);
    if (result && result.length === 0) {
      if (filters?.articleUuid) {
        filter = {
          filters: {
            uuid: filters.articleUuid,
          },
        };
        result = await ArticleService.getAllArticles(filter);
      }
    }
    return result;
  };

  const handlerFilterLocation = async (value) => {
    let filter = {
      filters: {
        name: value,
      },
    };

    let result = await LocationService.getAllLocations(filter);
    if (result && result.length === 0) {
      if (filters?.locationUuid) {
        filter = {
          filters: {
            uuid: filters.locationUuid,
          },
        };
        result = await LocationService.getAllLocations(filter);
      }
    }
    return result;
  };

  const handlerFilterTask = async (value) => {
    let filter = {
      filters: {
        name: value,
      },
    };

    let result = await TaskService.listAllTask(filter);
    if (result && result.length === 0) {
      if (filters?.taskUuid) {
        filter = {
          filters: {
            uuid: filters.taskUuid,
          },
        };
        result = await TaskService.listAllTask(filter);
      }
    }
    return result;
  };

  const handlerFilterBatch = async (value) => {
    let filter = {
      filters: {
        code: value,
      },
    };

    let result = await BatchService.getAllBatches(filter);
    if (result && result.length === 0) {
      if (filters?.batch) {
        filter = {
          filters: {
            uuid: filters.batch,
          },
        };
        result = await BatchService.getAllBatches(filter);
      }
    }
    return result;
  };

  const notifyAll = async () => {
    try {
      await confirmAllDailyPart({
        filters,
        sort: "date",
      });
    } catch (error) {}
  };

  const exportToExcel = async () => {
    const filter = {
      filters,
      sort: "date",
    };
    WorkingPartServices.listToExportDaily(filter).then((result) => {
      let total = 0;
      const data = result.map((item) => {
        total = total + item.totalTime;

        const status = PART_STATUS.find((st) => st.code === item.status);
        return {
          A: `${item.user.name} ${item.user.surname || ""}`,
          B: moment(item.date).format("DD-MM-yyyy"),
          C: converToTime(item.totalTime),
          D: msToHoursnumber(item.totalTime),
          E: t(status.label),
          // E: t(type.label),
        };
      });

      data.push({ A: "", B: "", C: "", D: "", E: "" });
      data.push({
        A: "TOTAL",
        B: "",
        C: converToTime(total),
        D: msToHoursnumber(total),
        E: "",
      });

      data.unshift({
        A: "Trabajador",
        B: "Fecha",
        C: "Tiempo trabajado",
        D: "Tiempo trabajado (horas)",
        E: "Estado",
        // E: "Tipo",
      });

      const filename = `Parte_export_${moment(new Date()).format(
        "DDMMyyyyHHmm"
      )}`;
      const sheetName = "Partes de trabajo";
      var ws = XLSX.utils.json_to_sheet(data, {
        header: ["A", "B", "C", "D"],
        skipHeader: true,
        UTC: true,
        dateNF: "dd-mm-yyyy hh:mm;@",
      });
      // dateNF:'mm/dd/yyyy;@',cellDates:true, raw: true}
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
      XLSX.writeFile(wb, filename + ".xlsx");
    });
  };


  return (
    <>
      <div className="row justify-content-center">
        <div className="col-12 col-md-11 marg-btm-1rem marg-top-40px">
          <h3 className="title">{t("forms.workingPart.title")}</h3>
        </div>
      </div>
      <BreadCrumb items={breads} />
      <div className="row justify-content-center">
        <div className="col-12 col-md-11">
          <Filter
            onFilterBatch={handlerFilterBatch}
            onFilterTask={handlerFilterTask}
            onFilterLocation={handlerFilterLocation}
            onFilterArticle={handlerFilterArticle}
            onFilterUser={handlerFilterUser}
            onSubmit={handlerOnSubmitFilter}
            onClean={handlerCleanFilter}
            isTechnical={isTechnical}
            viveros={viveros}
            initialValues={filters}
          />
        </div>
      </div>
      {!isTechnical && (
        <div className="row justify-content-center mt-4 no-margin">
          <div className="col-12 col-md-11 text-end ">
            <Can do={USER_PERMISSIONS.CONFIRM_PART}>
              <button
                type="button"
                className="btn btn-md btn-danger me-2"
                onClick={() => notifyAll()}
              >
                <i className="fa-solid fa-paper-plane icon-button"></i>{" "}
                {t("notifyAllParts")}
              </button>
            </Can>
            <button
              type="button"
              className="btn btn-md btn-secondary"
              onClick={() => exportToExcel()}
            >
              <i className="fa-solid fa-file-excel icon-button"></i>{" "}
              {t("exportFileExcel")}
            </button>
          </div>
        </div>
      )}

      <div className="row justify-content-center">
        <div className="col-12 col-md-11">
          <QueryClientProvider client={queryClient}>
            <List
              className="marg-top-1rem"
              filters={filters}
              isTechnical={isTechnical}
            />
          </QueryClientProvider>
        </div>
      </div>
    </>
  );
};

WorkingPartFilterListContainer.propTypes = {};

export default WorkingPartFilterListContainer;
